import { Box, TextField, TextFieldProps } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import moment from 'moment'

interface DateRangeFilterProps {
  fromDate: string | null
  toDate: string | null
  onFromDateChange: (date: string) => void
  onToDateChange: (date: string) => void
  dateFormat?: string
  filterDateFormat?: string
  maxMonthsBack?: number
  mediumViewport?: boolean
  size?: 'small' | 'medium'
  allowSameDate?: boolean
  minDate?: string
}

const DateRangeFilter = ({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  dateFormat = 'MM/DD/YYYY',
  filterDateFormat = 'MM/DD/YYYY',
  maxMonthsBack,
  mediumViewport = true,
  size = 'small',
  allowSameDate = false,
  minDate
}: DateRangeFilterProps) => {
  const getDate = (date: string | null, defaultMonths: number = 1) => {
    return date ? dayjs(date) : dayjs(new Date()).subtract(defaultMonths, 'month')
  }

  const getMaxDateForFrom = () => {
    const offset = allowSameDate ? 0 : 1
    return toDate
      ? dayjs(toDate).subtract(offset, 'day').format(dateFormat)
      : dayjs(new Date()).subtract(offset, 'day').format(filterDateFormat)
  }

  const getMinDateForTo = () => {
    const offset = allowSameDate ? 0 : 1
    const base = fromDate
      ? dayjs(fromDate).add(offset, 'day')
      : dayjs(new Date()).subtract(1, 'month').add(offset, 'day')
    // Never allow the "to" date to fall below the absolute floor.
    const clamped = minDate && base.isBefore(dayjs(minDate)) ? dayjs(minDate) : base
    return clamped.format(filterDateFormat)
  }

  // Absolute floor for the "from" picker: an explicit minDate wins over the relative
  const fromMinDate = minDate ?? (maxMonthsBack ? moment(new Date()).subtract(maxMonthsBack, 'months').format() : undefined)

  const datePickerProps = {
    inputFormat: dateFormat,
    renderInput: (params: TextFieldProps) => (
      <TextField
        size={size}
        {...params}
        // disable manual typing so an out-of-range date can't be set
        inputProps={minDate ? { ...params.inputProps, readOnly: true } : params.inputProps}
      />
    )
  }

  return (
    <Box display="flex" gap="10px" maxWidth={400} alignItems={'center'} mt={mediumViewport ? 0 : 5}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          {...datePickerProps}
          label="From"
          value={getDate(fromDate)}
          onChange={value => onFromDateChange(dayjs(value!).format(filterDateFormat))}
          minDate={fromMinDate}
          maxDate={getMaxDateForFrom()}
        />
      </LocalizationProvider>
      -
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          {...datePickerProps}
          label="To"
          value={getDate(toDate, -1)}
          onChange={value => onToDateChange(dayjs(value!).format(filterDateFormat))}
          minDate={getMinDateForTo()}
        />
      </LocalizationProvider>
    </Box>
  )
}

export default DateRangeFilter
